import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { useAuth } from '../contexts/AuthContext';
import { authApi, ViewMode } from '../api/auth';
import { formatApiError } from '../utils/errorUtils';
import { cryptoApi } from '../api/crypto';
import { requestE2eeUnlock } from '../utils/e2eeUnlockUi';
import { documentsApi } from '../api/documents';
import { familiesApi } from '../api/families';
import { clearCachedPrivateKey } from '../utils/e2eeClient';
import AiProviderPicker, { AiProviderPickerHandle } from './AiProviderPicker';
import {
  b64,
  decryptPrivateJwkFromStorage,
  encryptPrivateJwkForStorage,
  exportJwk,
  generateUserKeypair,
  importPrivateJwk,
  importPublicJwk,
  unwrapDekBytesForUser,
  wrapDekBytesForUser,
} from '../utils/e2eeCrypto';
import { canUsePlatformPasskeys, createPlatformPrfCredential, evalPrfSecret } from '../utils/webauthnPrf';
import {
  clearAllRememberedKeys,
  clearPasskeyWrappedJwk,
  clearRememberedPrivateKey,
  getPasskeyWrappedJwk,
  getRememberedPrivateKey,
  setRememberedPrivateKey,
} from '../utils/rememberedKeyStore';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

type ProfileTab = 'cont' | 'ai' | 'securitate';

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose }) => {
  const { user, refreshUser } = useAuth();
  const formId = useId();
  const [tab, setTab] = useState<ProfileTab>('cont');
  const [fullName, setFullName] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('simplified');
  const [saving, setSaving] = useState(false);
  const isAdvanced = viewMode === 'advanced';
  const [error, setError] = useState('');
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [rememberEnabled, setRememberEnabled] = useState(false);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const [initialRemember, setInitialRemember] = useState(false);
  const [initialPasskey, setInitialPasskey] = useState(false);
  const [e2eeBusy, setE2eeBusy] = useState(false);

  const [showRotate, setShowRotate] = useState(false);
  const [rotatePassphrase, setRotatePassphrase] = useState('');
  const [rotatePassphrase2, setRotatePassphrase2] = useState('');
  const [rotateDownloaded, setRotateDownloaded] = useState(false);
  const [rotateBusy, setRotateBusy] = useState(false);
  const rotatePassRef = useRef<HTMLInputElement | null>(null);
  const [rotateProgress, setRotateProgress] = useState<{ stage: string; processed: number; total: number } | null>(
    null
  );

  const aiPickerRef = useRef<AiProviderPickerHandle | null>(null);

  useEffect(() => {
    if (open && user) {
      setFullName(user.full_name || '');
      setViewMode(user.view_mode === 'advanced' ? 'advanced' : 'simplified');
      setError('');
      setTab('cont');
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    canUsePlatformPasskeys().then(setPasskeyAvailable).catch(() => setPasskeyAvailable(false));
    (async () => {
      try {
        const [pk, remembered] = await Promise.all([getPasskeyWrappedJwk(), getRememberedPrivateKey()]);
        const pkOn = Boolean(pk);
        const remOn = pkOn || Boolean(remembered);
        setPasskeyEnabled(pkOn);
        setRememberEnabled(remOn);
        setInitialPasskey(pkOn);
        setInitialRemember(remOn);
      } catch {
        setPasskeyEnabled(false);
        setRememberEnabled(false);
        setInitialPasskey(false);
        setInitialRemember(false);
      }
    })();
  }, [open]);

  const canFinishRotate = useMemo(() => {
    const p = rotatePassphrase.trim();
    return p.length >= 8 && p === rotatePassphrase2 && rotateDownloaded && !rotateBusy && !saving && !e2eeBusy;
  }, [rotatePassphrase, rotatePassphrase2, rotateDownloaded, rotateBusy, saving, e2eeBusy]);

  const generateStrongPassphrase = () => {
    const bytes = window.crypto.getRandomValues(new Uint8Array(18));
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const b = btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    const out = `edosar-${b}`;
    setRotatePassphrase(out);
    setRotatePassphrase2(out);
    setRotateDownloaded(false);
  };

  const downloadRotatePassphrase = () => {
    const blob = new Blob(
      [
        'eDosarMedical E2EE passphrase (ROTATION)\n',
        '\n',
        'Keep this secret. Anyone who has it can decrypt your encrypted documents.\n',
        'If you lose it, you may lose access to E2EE documents.\n',
        '\n',
        `Passphrase: ${rotatePassphrase}\n`,
      ],
      { type: 'text/plain;charset=utf-8' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edosar-passphrase-rotation.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setRotateDownloaded(true);
  };

  const startRotate = () => {
    setError('');
    setRotateProgress(null);
    setRotatePassphrase('');
    setRotatePassphrase2('');
    setRotateDownloaded(false);
    setRotateBusy(false);
    setShowRotate(true);
    window.setTimeout(() => rotatePassRef.current?.focus(), 0);
  };

  const finishRotate = async () => {
    if (!user) return;
    setRotateBusy(true);
    setError('');
    setRotateProgress({ stage: 'Pregătim rotația…', processed: 0, total: 0 });
    try {
      if (!rotateDownloaded) throw new Error('Descarcă passphrase-ul înainte de a continua.');
      if (rotatePassphrase.trim().length < 8) throw new Error('Passphrase prea scurt (minim 8 caractere).');
      if (rotatePassphrase !== rotatePassphrase2) throw new Error('Passphrase-urile nu coincid.');

      // Get old private key by prompting; used to unwrap existing envelopes.
      const oldPrivJwk = await getPrivateJwkByPrompting();
      const oldPriv = await importPrivateJwk(oldPrivJwk, { extractable: false });

      // Generate + store new keypair.
      setRotateProgress({ stage: 'Generăm chei noi…', processed: 0, total: 0 });
      const kp = await generateUserKeypair();
      const pubJwk = await exportJwk(kp.publicKey);
      const privJwk = await exportJwk(kp.privateKey);
      const enc = await encryptPrivateJwkForStorage(privJwk, rotatePassphrase.trim());

      setRotateProgress({ stage: 'Salvăm cheile în cont…', processed: 0, total: 0 });
      await cryptoApi.upsertMyKeys({
        public_jwk: JSON.stringify(pubJwk),
        encrypted_private_jwk: enc.encrypted_private_jwk,
        kdf_salt_b64: enc.kdf_salt_b64,
        kdf_iterations: enc.kdf_iterations,
        kdf_hash: enc.kdf_hash,
        priv_enc_alg: enc.priv_enc_alg,
        priv_enc_iv_b64: enc.priv_enc_iv_b64,
      });

      // Re-wrap every accessible per-file envelope for THIS user.
      //
      // After always-on E2EE (migration 0011), envelopes live in
      // `document_file_envelopes` (one per (file, user) pair) instead of
      // per-document. Rotation iterates every file the user can see
      // across every member in every family, downloads the ciphertext
      // bundle (which carries the per-user wrapped DEK in the
      // `X-Edosar-Encryption` header), unwraps with the OLD key,
      // re-wraps with the NEW public key, and upserts via
      // `upsertFileEnvelope`. Failures are best-effort; the user can
      // still open files that succeeded.
      setRotateProgress({ stage: 'Căutăm documente E2EE accesibile…', processed: 0, total: 0 });
      const families = await familiesApi.getFamilies();
      const memberIds: number[] = [];
      for (const f of families) {
        try {
          const members = await familiesApi.getMembers(f.id);
          for (const m of members) memberIds.push(m.id);
        } catch {
          // ignore; best-effort
        }
      }
      const uniqueMemberIds = Array.from(new Set(memberIds));

      const fileTargets: Array<{ documentId: number; fileId: number }> = [];
      for (const mid of uniqueMemberIds) {
        try {
          const docs = await documentsApi.getMemberDocuments(mid);
          for (const d of docs) {
            for (const f of d.files || []) {
              fileTargets.push({ documentId: d.id, fileId: f.id });
            }
          }
        } catch {
          // ignore
        }
      }
      const total = fileTargets.length;
      setRotateProgress({ stage: 'Re-împachetăm cheile fișierelor…', processed: 0, total });

      const newPub = await importPublicJwk(pubJwk);
      let processed = 0;
      for (const t of fileTargets) {
        try {
          const bundle = await documentsApi.downloadEncryptedFile(t.documentId, t.fileId);
          const dekBytes = await unwrapDekBytesForUser(oldPriv, b64.b64ToBytes(bundle.wrapped_dek));
          const wrapped = await wrapDekBytesForUser(newPub, dekBytes);
          await documentsApi.upsertFileEnvelope(t.documentId, t.fileId, {
            user_id: user.id,
            wrapped_dek: b64.bytesToB64(wrapped),
          });
        } catch {
          // Best-effort; user can still decrypt files that were rewrapped successfully.
        } finally {
          processed++;
          setRotateProgress({ stage: 'Re-împachetăm cheile fișierelor…', processed, total });
        }
      }

      // Rotation invalidates any remembered device key and cached key.
      await clearAllRememberedKeys();
      clearCachedPrivateKey();

      setShowRotate(false);
      setRotateProgress(null);
    } catch (e: any) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || 'Nu s-a putut roti cheia E2EE.');
    } finally {
      setRotateBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await authApi.updateMe({ full_name: fullName.trim() || null, view_mode: viewMode });

      // Persist any pending AI changes (typed key + provider/model). Surface its error if it fails so
      // the user sees what went wrong on the AI tab even if they hit Save from another tab.
      if (aiPickerRef.current) {
        const ok = await aiPickerRef.current.saveAll();
        if (!ok) {
          setTab('ai');
          return;
        }
      }

      // Apply E2EE device config only if user changed it.
      const rememberChanged = rememberEnabled !== initialRemember;
      const passkeyChanged = passkeyEnabled !== initialPasskey;
      if (rememberChanged || passkeyChanged) {
        // If remember is off, we can clear without passphrase.
        // Any transition that stores a key (remember on / switching passkey) requires passphrase once.
        let e2eeOk = true;
        if (!rememberEnabled) {
          try {
            await clearAllRememberedKeys();
          } catch (e: any) {
            // eslint-disable-next-line no-console
            console.error('[ProfileModal] failed to clear remembered keys', e);
            setError(e?.message || 'Nu s-au putut șterge cheile salvate pe acest device.');
            e2eeOk = false;
          }
        } else {
          e2eeOk = await applyRememberConfig(true, passkeyEnabled);
        }
        if (!e2eeOk) {
          // Surface the error to the user instead of silently closing the modal.
          setTab('securitate');
          return;
        }
        setInitialRemember(rememberEnabled);
        setInitialPasskey(passkeyEnabled);
      }

      await refreshUser();
      onClose();
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const getPrivateJwkByPrompting = async (): Promise<JsonWebKey> => {
    const keys = await cryptoApi.getMyKeys();
    if (!keys) throw new Error('Lipsesc cheile de criptare pentru acest cont.');
    const { passphrase } = await requestE2eeUnlock({
      title: 'Confirmă passphrase',
      message: 'Introdu passphrase-ul tău E2EE ca să putem aplica setările pe acest device.',
      showRememberOptions: false,
    });
    return await decryptPrivateJwkFromStorage(
      keys.encrypted_private_jwk,
      passphrase,
      keys.kdf_salt_b64,
      keys.kdf_iterations,
      keys.priv_enc_iv_b64
    );
  };

  const applyRememberConfig = async (nextRemember: boolean, nextPasskey: boolean): Promise<boolean> => {
    setE2eeBusy(true);
    setError('');
    try {
      if (!nextRemember) {
        await clearAllRememberedKeys();
        setRememberEnabled(false);
        setPasskeyEnabled(false);
        return true;
      }

      // Remember enabled: store either passkey-protected record or unprotected non-extractable CryptoKey.
      if (nextPasskey) {
        if (!passkeyAvailable) throw new Error('Passkey nu este disponibil pe acest device/browser.');

        // IMPORTANT order-of-operations:
        //   1) ask the user for their passphrase (modal click = fresh user activation)
        //   2) register + verify the passkey IMMEDIATELY while that activation is still valid
        //   3) THEN run the PBKDF2-heavy JWK decryption (no gesture required)
        //   4) wrap and persist
        // Doing the slow KDF before the WebAuthn calls would burn through the browser's
        // transient activation token (~5s in Chrome) and the OS prompt would silently fail.
        const { passphrase } = await requestE2eeUnlock({
          title: 'Confirmă passphrase',
          message: 'Introdu passphrase-ul E2EE; după Deblochează vei fi întrebat și de Passkey.',
          showRememberOptions: false,
        });
        if (!passphrase || !passphrase.trim()) throw new Error('Missing passphrase');

        const { credentialIdB64u } = await createPlatformPrfCredential();
        const prfSalt = window.crypto.getRandomValues(new Uint8Array(32));
        const secret = new Uint8Array(await evalPrfSecret(credentialIdB64u, prfSalt));

        // Now the heavy work is safe to run; no further user gesture needed.
        const keys = await cryptoApi.getMyKeys();
        if (!keys) throw new Error('Lipsesc cheile de criptare pentru acest cont.');
        const jwk = await decryptPrivateJwkFromStorage(
          keys.encrypted_private_jwk,
          passphrase.trim(),
          keys.kdf_salt_b64,
          keys.kdf_iterations,
          keys.priv_enc_iv_b64
        );

        const secretAb = secret.buffer.slice(secret.byteOffset, secret.byteOffset + secret.byteLength);
        const aes = await window.crypto.subtle.importKey('raw', secretAb, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const jwkBytes = new TextEncoder().encode(JSON.stringify(jwk));
        const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aes, jwkBytes);
        const { setPasskeyWrappedJwk } = await import('../utils/rememberedKeyStore');
        await setPasskeyWrappedJwk({
          credential_id_b64u: credentialIdB64u,
          prf_salt_b64: b64.bytesToB64(prfSalt),
          jwk_iv_b64: b64.bytesToB64(iv),
          jwk_ciphertext_b64: b64.bytesToB64(new Uint8Array(ct)),
        });
        await clearRememberedPrivateKey();
        setRememberEnabled(true);
        setPasskeyEnabled(true);
        return true;
      }

      // Remember enabled without passkey protection.
      const jwk = await getPrivateJwkByPrompting();
      const key = await importPrivateJwk(jwk, { extractable: false });
      await setRememberedPrivateKey(key);
      await clearPasskeyWrappedJwk();
      setRememberEnabled(true);
      setPasskeyEnabled(false);
      return true;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('[ProfileModal] applyRememberConfig failed', e);
      const isCancel =
        e?.name === 'NotAllowedError' ||
        e?.name === 'AbortError' ||
        e?.message === 'Missing passphrase' ||
        e?.message === 'Passkey creation cancelled' ||
        e?.message === 'Passkey assertion cancelled';
      // AES-GCM authentication-tag mismatch when decrypting the private JWK = wrong passphrase
      // (or, much rarer, a corrupted ciphertext). Translate it to a clear, actionable message
      // so the user doesn't see the bare "OperationError".
      const isWrongPassphrase =
        e?.name === 'OperationError' ||
        (typeof e?.message === 'string' &&
          /OperationError|decrypt|auth(?:entication)?\s*tag/i.test(e.message));
      setError(
        formatApiError(e?.response?.data?.detail) ||
          (isWrongPassphrase
            ? 'Passphrase incorect. Verifică ce ai introdus și reîncearcă.'
            : isCancel
              ? 'Operațiunea a fost anulată sau respinsă. Reîncearcă din Profil → Securitate.'
              : e?.message || 'Nu s-au putut salva setările E2EE.')
      );
      // Re-sync state from storage (best-effort) after errors.
      try {
        const [pk, remembered] = await Promise.all([getPasskeyWrappedJwk(), getRememberedPrivateKey()]);
        setPasskeyEnabled(Boolean(pk));
        setRememberEnabled(Boolean(pk) || Boolean(remembered));
      } catch {
        // ignore
      }
      return false;
    } finally {
      setE2eeBusy(false);
    }
  };

  // Tabs are the same in both modes — simplified users still need full access to
  // AI provider/key configuration. Only the *contents* of each tab adapt:
  // - Securitate hides the heavy "rotește chei E2EE" panel in simplified mode
  // - Cont gains the view-mode picker
  const tabs: { id: ProfileTab; label: string }[] = [
    { id: 'cont', label: 'Cont' },
    { id: 'ai', label: 'AI' },
    { id: 'securitate', label: isAdvanced ? 'Securitate' : 'Acces rapid' },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Profil"
      headerExtra={
        <div
          className="tabs-bar tabs-bar--pills profile-modal-tabs"
          role="tablist"
          aria-label="Secțiuni profil"
        >
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`${formId}-panel-${t.id}`}
                id={`${formId}-tab-${t.id}`}
                className="tabs-bar__tab"
                onClick={() => {
                  setError('');
                  setTab(t.id);
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      }
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : 'Salvează'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {error && <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>}

        <div
          role="tabpanel"
          id={`${formId}-panel-cont`}
          aria-labelledby={`${formId}-tab-cont`}
          hidden={tab !== 'cont'}
          style={tab === 'cont' ? undefined : { display: 'none' }}
        >
            <div className="form-group">
              <label className="form-label" htmlFor="profile-email">
                Email
              </label>
              <input id="profile-email" className="form-input" type="email" value={user?.email || ''} disabled />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="profile-fullname">
                Nume afișat
              </label>
              <input
                id="profile-fullname"
                className="form-input"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Prenume Nume"
                autoComplete="name"
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <div className="form-label" style={{ marginBottom: 6 }}>
                Mod de afișare
              </div>
              <div
                className="muted"
                style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-3)' }}
              >
                {isAdvanced
                  ? 'Modul avansat afișează toate detaliile tehnice: text OCR, clasificare AI, opțiuni de procesare, gestionare chei. Util dacă ești medic sau un utilizator avansat.'
                  : 'Modul simplificat (recomandat) afișează doar ce ai nevoie: documentele tale, sumar AI și controale esențiale. Poți comuta oricând la modul avansat.'}
              </div>

              <div
                role="radiogroup"
                aria-label="Mod de afișare"
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}
              >
                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: 'var(--sp-3)',
                    border: `1.5px solid ${viewMode === 'simplified' ? 'var(--color-primary-500)' : 'var(--color-slate-200)'}`,
                    borderRadius: 'var(--radius-md)',
                    background:
                      viewMode === 'simplified' ? 'var(--color-primary-50)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="profile-view-mode"
                    value="simplified"
                    checked={viewMode === 'simplified'}
                    onChange={() => setViewMode('simplified')}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                      Simplificat (recomandat)
                    </div>
                    <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>
                      Vezi doar lucrurile esențiale: documentele, sumarul AI și familia.
                    </div>
                  </span>
                </label>

                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: 'var(--sp-3)',
                    border: `1.5px solid ${viewMode === 'advanced' ? 'var(--color-primary-500)' : 'var(--color-slate-200)'}`,
                    borderRadius: 'var(--radius-md)',
                    background:
                      viewMode === 'advanced' ? 'var(--color-primary-50)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="profile-view-mode"
                    value="advanced"
                    checked={viewMode === 'advanced'}
                    onChange={() => setViewMode('advanced')}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Avansat</div>
                    <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>
                      Vezi tot: text OCR brut, clasificare AI detaliată, opțiuni de procesare, AI configurabil, chei E2EE.
                    </div>
                  </span>
                </label>
              </div>
            </div>
        </div>

        <div
          role="tabpanel"
          id={`${formId}-panel-ai`}
          aria-labelledby={`${formId}-tab-ai`}
          hidden={tab !== 'ai'}
          style={tab === 'ai' ? undefined : { display: 'none' }}
        >
          <div className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-3)' }}>
            {isAdvanced
              ? 'Alege furnizorul AI și modelul folosit pentru chat, rezumate și clasificare. Cheia API este stocată criptat pe server.'
              : 'Alege ce AI (ChatGPT, Gemini, etc.) folosește aplicația pentru a-ți rezuma și explica documentele. Cheia ta este stocată criptat.'}
          </div>
          <AiProviderPicker mode="profile" ref={aiPickerRef} />
        </div>

        <div
          role="tabpanel"
          id={`${formId}-panel-securitate`}
          aria-labelledby={`${formId}-tab-securitate`}
          hidden={tab !== 'securitate'}
          style={tab === 'securitate' ? undefined : { display: 'none' }}
        >
            {isAdvanced && (
            <div className="form-group" style={{ marginBottom: 'var(--sp-4)' }}>
              <div className="form-label" style={{ marginBottom: 6 }}>
                Chei E2EE (cont)
              </div>
              <div className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-3)' }}>
                Rotește/regenerează cheile E2EE ale contului. După rotație, aplicația va re-împacheta cheile
                documentelor ca să rămână decriptabile.
              </div>

              {!showRotate ? (
                <button type="button" className="btn btn-secondary" onClick={startRotate} disabled={saving || e2eeBusy}>
                  Rotește cheile E2EE
                </button>
              ) : (
                <div className="card" style={{ padding: 'var(--sp-4)', borderRadius: 'var(--radius-xl)' }}>
                  <div className="alert" style={{ marginBottom: 'var(--sp-3)' }}>
                    Dacă pierzi noul passphrase, poți pierde accesul la documentele E2EE. Este obligatoriu să îl
                    descarci.
                  </div>

                  <div className="form-group">
                    <label className="form-label">Passphrase nou</label>
                    <input
                      ref={rotatePassRef}
                      className="form-input"
                      type="password"
                      value={rotatePassphrase}
                      onChange={(e) => {
                        setRotatePassphrase(e.target.value);
                        setRotateDownloaded(false);
                      }}
                      disabled={rotateBusy}
                      placeholder="Minim 8 caractere"
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={generateStrongPassphrase}
                        disabled={rotateBusy}
                      >
                        Generează
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Confirmă passphrase</label>
                    <input
                      className="form-input"
                      type="password"
                      value={rotatePassphrase2}
                      onChange={(e) => {
                        setRotatePassphrase2(e.target.value);
                        setRotateDownloaded(false);
                      }}
                      disabled={rotateBusy}
                      placeholder="Repetă passphrase"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={downloadRotatePassphrase}
                      disabled={
                        rotateBusy || rotatePassphrase.trim().length < 8 || rotatePassphrase !== rotatePassphrase2
                      }
                      title="Obligatoriu înainte de finalizare"
                    >
                      {rotateDownloaded ? 'Re-descarcă passphrase ✓' : 'Descarcă passphrase (obligatoriu)'}
                    </button>
                    <button type="button" className="btn btn-danger" onClick={finishRotate} disabled={!canFinishRotate}>
                      {rotateBusy ? 'Se rotește…' : 'Finalizează rotația'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowRotate(false)}
                      disabled={rotateBusy}
                    >
                      Anulează
                    </button>
                  </div>

                  {rotateProgress && (
                    <div className="muted" style={{ marginTop: 'var(--sp-3)', fontSize: 'var(--text-xs)' }}>
                      {rotateProgress.stage}
                      {rotateProgress.total > 0 ? ` (${rotateProgress.processed}/${rotateProgress.total})` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            <div className="form-group" style={{ marginBottom: 0 }}>
              <div className="form-label" style={{ marginBottom: 6 }}>
                {isAdvanced ? 'Configurare E2EE pe acest device' : 'Acces rapid pe acest dispozitiv'}
              </div>
              <div className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-3)' }}>
                {isAdvanced
                  ? 'Controlează dacă cheia este salvată în browser și dacă este protejată cu Passkey (Hello/FaceID/TouchID).'
                  : 'Dacă activezi această opțiune, nu va mai trebui să introduci passphrase-ul de fiecare dată când deschizi documentele protejate pe acest dispozitiv.'}
              </div>

              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={rememberEnabled}
                  disabled={saving || e2eeBusy}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setRememberEnabled(next);
                    // In simplified mode the passkey checkbox is hidden; we auto-enable
                    // passkey protection when the device supports it (it's strictly better
                    // than leaving the key unprotected in storage).
                    if (!next) {
                      setPasskeyEnabled(false);
                    } else if (!isAdvanced && passkeyAvailable) {
                      setPasskeyEnabled(true);
                    }
                  }}
                  style={{ marginTop: 3 }}
                />
                <span style={{ fontSize: 'var(--text-sm)' }}>
                  {isAdvanced
                    ? 'Ține minte cheia pe acest device (nu vei mai introduce passphrase-ul).'
                    : 'Ține minte parola mea pe acest dispozitiv'}
                </span>
              </label>

              {isAdvanced && (
                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    marginTop: 10,
                    opacity: rememberEnabled ? 1 : 0.55,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={passkeyEnabled}
                    disabled={saving || e2eeBusy || !rememberEnabled || !passkeyAvailable}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setPasskeyEnabled(next);
                    }}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 'var(--text-sm)' }}>
                    Protejează cu Passkey (Hello/FaceID/TouchID) pentru a debloca cheia salvată.
                    {!passkeyAvailable ? ' (Indisponibil pe acest browser/dispozitiv.)' : ''}
                  </span>
                </label>
              )}

              {!isAdvanced && rememberEnabled && passkeyAvailable && (
                <div className="muted" style={{ marginTop: 10, fontSize: 'var(--text-xs)' }}>
                  Vom folosi Windows Hello / FaceID / TouchID pentru a proteja accesul rapid.
                </div>
              )}

              {e2eeBusy && (
                <div className="muted" style={{ marginTop: 10, fontSize: 'var(--text-xs)' }}>
                  Se aplică setările…
                </div>
              )}
            </div>
        </div>
      </form>
    </Modal>
  );
};

export default ProfileModal;
